import { Trans, useLingui } from "@lingui/react";
import { WIcon } from "@mobility-item-page/client";
import type { WarpModal } from "@warp-ds/elements/modal";
import { Modal, ModalFooter, ModalHeader } from "@warp-ds/elements/react/modal";
import { Button } from "@warp-ds/react";
import { useImperativeHandle, useRef, useState } from "react";
import EidBlocket from "../../assets/eid-blocket.svg";
import EidFinn from "../../assets/eid-finn.svg";
import { useValidDealerItemContext } from "../../context/DealerItemProviderContext.tsx";

type ModalVariant = "sendMessage" | "reserveCar";

export type EidModalHandle = {
  open: (variant: ModalVariant) => void;
};

export const EidModal = ({ ref }: { ref?: React.Ref<EidModalHandle> }) => {
  const modalRef = useRef<WarpModal>(null);
  const [nextUrl, setNextUrl] = useState("/auth/login/eid");
  const [variation, setVariation] = useState<ModalVariant>("sendMessage");
  const { i18n } = useLingui();
  const {
    external: { brand, isMobile, eidAuth },
  } = useValidDealerItemContext();

  const openWith = (variant: ModalVariant) => {
    const { origin, pathname } = window.location;
    const url = new URL(`${origin}/auth/login/eid`);
    const redirectUrl = variant === "sendMessage" ? `${origin}${pathname}?contact` : `${origin}${pathname}`;

    url.searchParams.set("redirectUrl", redirectUrl);
    url.searchParams.set("cancelUrl", `${origin}${pathname}`);

    setNextUrl(url.toString());
    setVariation(variant);
    modalRef.current?.open();
  };

  useImperativeHandle(ref, () => ({
    open: openWith,
  }));

  const onClick = () => {
    window.location.href = nextUrl;
  };

  return (
    <Modal ref={modalRef}>
      <ModalHeader slot="header" title={i18n.t({ id: "dealer.eidModal.title", message: "Verify your identity" })} />
      <div slot="content" className="flex flex-col items-center justify-center text-center gap-24">
        {brand.toLowerCase() === "blocket" && (
          <img src={EidBlocket} alt={"Hand som håller en mobiltelefon med BankID-appen öppen på skärmen"} />
        )}
        {brand.toLowerCase() === "finn" && <img src={EidFinn} className="w-[200px]" alt={"BankID logo"} />}
        {eidAuth?.status === "EXPIRED" && (
          <h3>
            <Trans id="dealer.eidModal.eidExpired" message="Your validation has expired" />
          </h3>
        )}
        {variation === "sendMessage" ? (
          <Trans
            id="dealer.eidModal.description.sendMessage"
            message="Before contacting someone, you need to verify your account. This lets the person on the other end feel confident about who they are talking to."
          />
        ) : (
          <Trans
            id="dealer.eidModal.description.reserveCar"
            message="Before reserving the car, you need to verify your account. This lets the dealer confirm who is doing the reservation."
          />
        )}
      </div>
      <ModalFooter slot="footer" className="flex justify-center">
        <Button fullWidth={isMobile} className="flex items-center gap-8" primary onClick={onClick}>
          {brand.toLowerCase() === "finn" && <WIcon name="BankIdNo" />}
          <Trans
            id="dealer.eidModal.verifyButton"
            message="Verify with Electronic ID"
            comment="Text for button to go to the BankID / MitId validation page. The text should contain the correct validator for each country (eg. bankId for NB and SV, and MitID for DA)"
          />
        </Button>
      </ModalFooter>
    </Modal>
  );
};
